import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  CircularProgress,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Tooltip,
  Grid,
  Divider,
} from "@mui/material";
import {
  Search,
  MoreVert,
  PlayArrow,
  Stop,
  RestartAlt,
  Delete,
  DeleteForever,
  RestoreFromTrash,
  Block,
  CheckCircle,
  Build,
  SwapHoriz,
  Info,
  Web as WebIcon,
  OpenInNew,
} from "@mui/icons-material";
import {
  fetchAdminSites,
  adminUpdateSiteStatus,
  adminStartSite,
  adminStopSite,
  adminRestartSite,
  adminSoftDeleteSite,
  adminPermanentDeleteSite,
  adminRestoreSite,
  adminTransferSite,
  fetchAdminSiteDetails,
  clearAdminError,
  clearAdminSuccess,
  clearSelectedSite,
} from "../../redux/slices/adminSlice";
import { SPACING } from "../../theme/constants";

const AdminSites = () => {
  const dispatch = useDispatch();
  const {
    sites,
    sitesPagination,
    selectedSite,
    loading,
    actionLoading,
    error,
    successMessage,
  } = useSelector((state) => state.admin);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    page: 1,
    limit: 20,
  });

  // Action menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuSite, setMenuSite] = useState(null);

  // Dialogs
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferUserId, setTransferUserId] = useState("");
  const [permanentDeleteConfirm, setPermanentDeleteConfirm] = useState("");

  useEffect(() => {
    dispatch(fetchAdminSites(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => dispatch(clearAdminSuccess()), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => dispatch(clearAdminError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const refreshSites = useCallback(() => {
    dispatch(fetchAdminSites(filters));
  }, [dispatch, filters]);

  const handleMenuOpen = (event, site) => {
    setMenuAnchor(event.currentTarget);
    setMenuSite(site);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleViewDetails = (site) => {
    dispatch(fetchAdminSiteDetails(site.id));
    setDetailsOpen(true);
    handleMenuClose();
  };

  const openConfirm = (action) => {
    setConfirmAction(action);
    setConfirmOpen(true);
    setPermanentDeleteConfirm("");
    handleMenuClose();
  };

  const executeAction = async () => {
    const siteId = confirmAction?.siteId;
    if (!siteId) return;

    switch (confirmAction?.type) {
      case "start":
        await dispatch(adminStartSite(siteId));
        break;
      case "stop":
        await dispatch(adminStopSite(siteId));
        break;
      case "restart":
        await dispatch(adminRestartSite(siteId));
        break;
      case "suspend":
        await dispatch(adminUpdateSiteStatus({ siteId, status: "SUSPENDED" }));
        break;
      case "unsuspend":
        await dispatch(adminUpdateSiteStatus({ siteId, status: "ACTIVE" }));
        break;
      case "maintenance":
        await dispatch(
          adminUpdateSiteStatus({ siteId, status: "MAINTENANCE" }),
        );
        break;
      case "softDelete":
        await dispatch(adminSoftDeleteSite(siteId));
        break;
      case "permanentDelete":
        await dispatch(adminPermanentDeleteSite(siteId));
        break;
      case "restore":
        await dispatch(adminRestoreSite(siteId));
        break;
      default:
        break;
    }
    setConfirmOpen(false);
    setConfirmAction(null);
    refreshSites();
  };

  const handleTransfer = async () => {
    if (!menuSite || !transferUserId.trim()) return;
    await dispatch(
      adminTransferSite({
        siteId: menuSite.id,
        newOwnerId: transferUserId.trim(),
      }),
    );
    setTransferOpen(false);
    setTransferUserId("");
    setMenuSite(null);
    refreshSites();
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: "success",
      SUSPENDED: "error",
      PENDING: "warning",
      MAINTENANCE: "info",
      UPDATING: "info",
      DELETED: "default",
    };
    return colors[status] || "default";
  };

  const getActionLabel = () => {
    switch (confirmAction?.type) {
      case "start":
        return {
          title: "Start Site",
          desc: "This will start the site containers.",
          color: "primary",
        };
      case "stop":
        return {
          title: "Stop Site",
          desc: "This will stop the site containers. The site will be inaccessible.",
          color: "warning",
        };
      case "restart":
        return {
          title: "Restart Site",
          desc: "This will restart all site containers.",
          color: "info",
        };
      case "suspend":
        return {
          title: "Suspend Site",
          desc: "This will suspend the site and stop its containers. The owner will not be able to access it.",
          color: "warning",
        };
      case "unsuspend":
        return {
          title: "Unsuspend Site",
          desc: "This will reactivate the site and start its containers.",
          color: "success",
        };
      case "maintenance":
        return {
          title: "Set Maintenance Mode",
          desc: "This will put the site in maintenance mode.",
          color: "info",
        };
      case "softDelete":
        return {
          title: "Delete Site",
          desc: "This will soft-delete the site. It can be restored within 30 days.",
          color: "error",
        };
      case "permanentDelete":
        return {
          title: "Permanently Delete Site",
          desc: "This will PERMANENTLY delete the site, all its data, containers, volumes, and backups. This action CANNOT be undone.",
          color: "error",
        };
      case "restore":
        return {
          title: "Restore Site",
          desc: "This will restore the deleted site and reactivate its containers.",
          color: "success",
        };
      default:
        return { title: "Confirm", desc: "", color: "primary" };
    }
  };

  const actionInfo = getActionLabel();

  return (
    <Box>
      <Box sx={{ mb: SPACING.lg }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: SPACING.xs }}>
          Site Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Full control over all sites — start, stop, suspend, delete, transfer
          ownership
        </Typography>
      </Box>

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearAdminSuccess())}
        >
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearAdminError())}
        >
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent
          sx={{
            py: 2,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            placeholder="Search sites..."
            value={filters.search}
            onChange={(e) =>
              setFilters({ ...filters, search: e.target.value, page: 1 })
            }
            size="small"
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value, page: 1 })
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
              <MenuItem value="DELETED">Deleted</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          <Chip
            label={`${sitesPagination?.total || sites.length} sites`}
            variant="outlined"
            size="small"
            sx={{ px: 1.5, fontWeight: 500 }}
          />
        </CardContent>
      </Card>

      {/* Sites Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Site</TableCell>
                    <TableCell>Domain</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Environment</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sites.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No sites found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sites.map((site) => (
                      <TableRow key={site.id} hover>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <WebIcon fontSize="small" color="action" />
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {site.name || site.domain}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {site.id?.substring(0, 12)}...
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography variant="body2">
                              {site.customDomain || site.domain}
                            </Typography>
                            {site.status === "ACTIVE" && (
                              <Tooltip title="Visit site">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    window.open(
                                      `https://${site.customDomain || site.domain}`,
                                      "_blank",
                                      "noopener",
                                    )
                                  }
                                >
                                  <OpenInNew sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {site.user
                              ? `${site.user.firstName || ""} ${site.user.lastName || ""}`.trim() ||
                                site.user.email
                              : "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {site.user?.email || ""}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={site.environment || "SANDBOX"}
                            size="small"
                            variant="outlined"
                            color={
                              site.environment === "PRODUCTION"
                                ? "error"
                                : site.environment === "STAGING"
                                  ? "warning"
                                  : "default"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={site.status}
                            size="small"
                            color={getStatusColor(site.status)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(site.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Actions">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, site)}
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {sitesPagination && (
              <TablePagination
                component="div"
                count={sitesPagination.total || 0}
                page={(sitesPagination.page || 1) - 1}
                onPageChange={(_, newPage) =>
                  setFilters({ ...filters, page: newPage + 1 })
                }
                rowsPerPage={filters.limit}
                onRowsPerPageChange={(e) =>
                  setFilters({
                    ...filters,
                    limit: parseInt(e.target.value, 10),
                    page: 1,
                  })
                }
                rowsPerPageOptions={[10, 20, 50]}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* ==================== Actions Menu ==================== */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { minWidth: 220 } }}
      >
        {/* View Details */}
        <MenuItem onClick={() => handleViewDetails(menuSite)}>
          <ListItemIcon>
            <Info fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>

        <Divider />

        {/* Container Controls */}
        {menuSite?.status !== "DELETED" && [
          <MenuItem
            key="start"
            onClick={() => openConfirm({ type: "start", siteId: menuSite?.id })}
            disabled={menuSite?.status === "ACTIVE"}
          >
            <ListItemIcon>
              <PlayArrow fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Start</ListItemText>
          </MenuItem>,
          <MenuItem
            key="stop"
            onClick={() => openConfirm({ type: "stop", siteId: menuSite?.id })}
            disabled={menuSite?.status === "SUSPENDED"}
          >
            <ListItemIcon>
              <Stop fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Stop</ListItemText>
          </MenuItem>,
          <MenuItem
            key="restart"
            onClick={() =>
              openConfirm({ type: "restart", siteId: menuSite?.id })
            }
            disabled={menuSite?.status !== "ACTIVE"}
          >
            <ListItemIcon>
              <RestartAlt fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText>Restart</ListItemText>
          </MenuItem>,
        ]}

        <Divider />

        {/* Status Changes */}
        {menuSite?.status === "ACTIVE" && (
          <MenuItem
            onClick={() =>
              openConfirm({ type: "suspend", siteId: menuSite?.id })
            }
          >
            <ListItemIcon>
              <Block fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Suspend</ListItemText>
          </MenuItem>
        )}
        {menuSite?.status === "SUSPENDED" && (
          <MenuItem
            onClick={() =>
              openConfirm({ type: "unsuspend", siteId: menuSite?.id })
            }
          >
            <ListItemIcon>
              <CheckCircle fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Unsuspend</ListItemText>
          </MenuItem>
        )}
        {menuSite?.status !== "DELETED" &&
          menuSite?.status !== "MAINTENANCE" && (
            <MenuItem
              onClick={() =>
                openConfirm({ type: "maintenance", siteId: menuSite?.id })
              }
            >
              <ListItemIcon>
                <Build fontSize="small" />
              </ListItemIcon>
              <ListItemText>Maintenance Mode</ListItemText>
            </MenuItem>
          )}

        {/* Restore deleted site */}
        {menuSite?.status === "DELETED" && (
          <MenuItem
            onClick={() =>
              openConfirm({ type: "restore", siteId: menuSite?.id })
            }
          >
            <ListItemIcon>
              <RestoreFromTrash fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Restore Site</ListItemText>
          </MenuItem>
        )}

        <Divider />

        {/* Transfer Ownership */}
        {menuSite?.status !== "DELETED" && (
          <MenuItem
            onClick={() => {
              setTransferOpen(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <SwapHoriz fontSize="small" />
            </ListItemIcon>
            <ListItemText>Transfer Ownership</ListItemText>
          </MenuItem>
        )}

        {/* Delete */}
        {menuSite?.status !== "DELETED" && (
          <MenuItem
            onClick={() =>
              openConfirm({ type: "softDelete", siteId: menuSite?.id })
            }
          >
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>
              Delete Site
            </ListItemText>
          </MenuItem>
        )}

        {/* Permanent Delete */}
        <MenuItem
          onClick={() =>
            openConfirm({ type: "permanentDelete", siteId: menuSite?.id })
          }
        >
          <ListItemIcon>
            <DeleteForever fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: "error.main", fontWeight: 600 }}>
            Permanent Delete
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* ==================== Confirm Dialog ==================== */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography
            variant="h6"
            fontWeight={600}
            color={`${actionInfo.color}.main`}
          >
            {actionInfo.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {actionInfo.desc}
          </Typography>
          {menuSite && (
            <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Site:</strong> {menuSite.name || menuSite.domain}
              </Typography>
              <Typography variant="body2">
                <strong>Domain:</strong> {menuSite.domain}
              </Typography>
              <Typography variant="body2">
                <strong>Owner:</strong> {menuSite.user?.email || "—"}
              </Typography>
            </Box>
          )}
          {confirmAction?.type === "permanentDelete" && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                This is irreversible! All site data, databases, files, backups,
                and containers will be permanently destroyed.
              </Alert>
              <TextField
                fullWidth
                label='Type "DELETE" to confirm'
                value={permanentDeleteConfirm}
                onChange={(e) => setPermanentDeleteConfirm(e.target.value)}
                size="small"
                error={
                  permanentDeleteConfirm.length > 0 &&
                  permanentDeleteConfirm !== "DELETE"
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={actionInfo.color}
            onClick={executeAction}
            disabled={
              actionLoading ||
              (confirmAction?.type === "permanentDelete" &&
                permanentDeleteConfirm !== "DELETE")
            }
          >
            {actionLoading ? <CircularProgress size={20} /> : actionInfo.title}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== Transfer Dialog ==================== */}
      <Dialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Transfer Site Ownership
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Transfer <strong>{menuSite?.name || menuSite?.domain}</strong> to a
            different user.
          </Typography>
          <TextField
            fullWidth
            label="New Owner User ID"
            placeholder="Enter the user ID to transfer to"
            value={transferUserId}
            onChange={(e) => setTransferUserId(e.target.value)}
            size="small"
            helperText="Enter the exact user ID (cuid) of the new owner"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTransfer}
            disabled={!transferUserId.trim() || actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : "Transfer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== Site Details Dialog ==================== */}
      <Dialog
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          dispatch(clearSelectedSite());
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Site Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {actionLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedSite ? (
            <Box>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedSite.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Domain
                  </Typography>
                  <Typography variant="body1">{selectedSite.domain}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box>
                    <Chip
                      label={selectedSite.status}
                      size="small"
                      color={getStatusColor(selectedSite.status)}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Environment
                  </Typography>
                  <Typography variant="body1">
                    {selectedSite.environment}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Owner
                  </Typography>
                  <Typography variant="body1">
                    {selectedSite.user
                      ? `${selectedSite.user.firstName || ""} ${selectedSite.user.lastName || ""}`.trim() ||
                        selectedSite.user.email
                      : "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedSite.user?.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Plan
                  </Typography>
                  <Typography variant="body1">
                    {selectedSite.plan?.name || "None"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    WordPress Version
                  </Typography>
                  <Typography variant="body1">
                    {selectedSite.wpVersion || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    SSL
                  </Typography>
                  <Typography variant="body1">
                    {selectedSite.sslEnabled ? "Enabled" : "Disabled"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Storage Used
                  </Typography>
                  <Typography variant="body1">
                    {selectedSite.storageUsedMB || 0} MB
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedSite.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedSite.updatedAt).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Backups / Deployments
                  </Typography>
                  <Typography variant="body1">
                    {selectedSite._count?.backups || 0} /{" "}
                    {selectedSite._count?.deployments || 0}
                  </Typography>
                </Grid>
                {selectedSite.customDomain && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Custom Domain
                    </Typography>
                    <Typography variant="body1">
                      {selectedSite.customDomain}
                    </Typography>
                  </Grid>
                )}
                {selectedSite.workerId && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Worker ID
                    </Typography>
                    <Typography variant="body1">
                      {selectedSite.workerId}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ mt: 3, mb: 1 }}
              >
                Site ID
              </Typography>
              <Typography
                variant="body2"
                fontFamily="monospace"
                sx={{ p: 1, bgcolor: "background.default", borderRadius: 1 }}
              >
                {selectedSite.id}
              </Typography>
            </Box>
          ) : (
            <Typography color="text.secondary">
              No site data available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setDetailsOpen(false);
              dispatch(clearSelectedSite());
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSites;
